import React, { useState, useEffect } from 'react';
import { scheduledPostApi, linkedInApi, ScheduledPost, LinkedInStatus } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { useToast } from '../sectepe-ui/index';
import { PageHeader, useConfirm, RelatedLinks, useFocusParam } from '../components/ui';
import { ideaBacklinks } from '../linkedin/ideaLinks';
import { FlowStepBar, LinkedInConnectionCard, useLinkedInCallbackParams, useLinkedInFlow } from '../linkedin';

/**
 * Freigabe-Warteschlange. Rechte (Paket 2): Genehmigen, Ablehnen, auf
 * LinkedIn posten und als gepostet markieren brauchen linkedin.freigeben;
 * Inhalt und Datum eines ausstehenden Posts aendern geht mit
 * linkedin.bearbeiten.
 */
const ScheduledPostsPage: React.FC = () => {
  const { user } = useAuth();
  const darfFreigeben = useBerechtigung().darf('linkedin.freigeben');
  const { push } = useToast();
  const { confirm, prompt } = useConfirm();
  const { refresh } = useLinkedInFlow();
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('');
  const [linkedInStatus, setLinkedInStatus] = useState<LinkedInStatus | null>(null);
  const [publishingPostId, setPublishingPostId] = useState<string | null>(null);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editingDatePostId, setEditingDatePostId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState('');

  const currentUserName = user?.name || 'Unknown';

  // Rueckkehr vom LinkedIn-OAuth (Backend leitet hierher): Toast + URL aufraeumen.
  useLinkedInCallbackParams(() => { loadData(); refresh(); });

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [postsResult, statusResult] = await Promise.all([
        scheduledPostApi.getScheduledPosts({
          status: statusFilter || undefined,
          contentType: contentTypeFilter || undefined,
        }),
        linkedInApi.getStatus().catch(() => ({ configured: false, connections: [] })),
      ]);
      setPosts(postsResult.posts);
      setLinkedInStatus(statusResult);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Freigabe-Warteschlange');
    } finally {
      setLoading(false);
    }
  };

  useFocusParam('id', !loading && posts.length > 0);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, contentTypeFilter]);

  const handleApprove = async (postId: string) => {
    if (!(await confirm({ title: 'Post genehmigen?', message: 'Genehmigte Posts werden zum geplanten Zeitpunkt veröffentlicht.', confirmLabel: 'Genehmigen' }))) return;
    try {
      setLoading(true);
      await scheduledPostApi.approvePost(postId, currentUserName);
      push({ message: 'Post genehmigt!', severity: 'success' });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Genehmigen');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (postId: string) => {
    const reason = await prompt({ title: 'Post ablehnen', label: 'Ablehnungsgrund (optional)', multiline: true, confirmLabel: 'Ablehnen', tone: 'danger' });
    if (reason === null) return;
    try {
      setLoading(true);
      await scheduledPostApi.rejectPost(postId, currentUserName, reason || undefined);
      push({ message: 'Post abgelehnt.', severity: 'success' });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Ablehnen');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPosted = async (postId: string) => {
    if (!(await confirm({ title: 'Als veröffentlicht markieren?', message: 'Nutze das, wenn du den Post selbst auf LinkedIn gepostet hast.', confirmLabel: 'Markieren' }))) return;
    try {
      setLoading(true);
      await scheduledPostApi.markAsPosted(postId);
      push({ message: 'Post als veröffentlicht markiert!', severity: 'success' });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Markieren');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishToLinkedIn = async (postId: string) => {
    if (!(await confirm({ title: 'Jetzt auf LinkedIn veröffentlichen?', message: 'Der Post geht sofort über das verbundene Konto raus.', confirmLabel: 'Veröffentlichen' }))) return;
    try {
      setPublishingPostId(postId);
      await linkedInApi.publishPost(postId);
      push({ message: 'Post erfolgreich auf LinkedIn veröffentlicht!', severity: 'success' });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Veröffentlichen auf LinkedIn');
    } finally {
      setPublishingPostId(null);
    }
  };

  const handleDelete = async (postId: string) => {
    if (!(await confirm({ title: 'Geplanten Post löschen?', confirmLabel: 'Löschen', tone: 'danger' }))) return;
    try {
      setLoading(true);
      await scheduledPostApi.deleteScheduledPost(postId);
      push({ message: 'Post gelöscht.', severity: 'success' });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (post: ScheduledPost) => {
    setEditingPostId(post.id);
    setEditContent(post.generatedContent.content);
  };

  const handleSaveEdit = async () => {
    if (!editingPostId) return;
    try {
      setLoading(true);
      await linkedInApi.updatePostContent(editingPostId, editContent);
      setEditingPostId(null);
      push({ message: 'Inhalt aktualisiert!', severity: 'success' });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren des Inhalts');
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateEdit = (post: ScheduledPost) => {
    setEditingDatePostId(post.id);
    setEditDate(new Date(post.scheduledDate).toISOString().slice(0, 16));
  };

  const handleSaveDateEdit = async () => {
    if (!editingDatePostId) return;
    try {
      setLoading(true);
      await scheduledPostApi.updateScheduledDate(editingDatePostId, new Date(editDate).toISOString());
      setEditingDatePostId(null);
      push({ message: 'Datum aktualisiert!', severity: 'success' });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren des Datums');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  const getDaysUntilScheduled = (scheduledDate: string) => {
    const now = new Date();
    const scheduled = new Date(scheduledDate);
    const diffTime = scheduled.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Ausstehend';
      case 'approved': return 'Genehmigt';
      case 'rejected': return 'Abgelehnt';
      case 'posted': return 'Veröffentlicht';
      default: return status;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'badge badge-warning';
      case 'approved': return 'badge badge-success';
      case 'rejected': return 'badge badge-error';
      case 'posted': return 'badge badge-info';
      default: return 'badge badge-neutral';
    }
  };

  const hasActiveLinkedIn = linkedInStatus?.connections?.some((c) => c.isActive) || false;

  return (
    <div className="page-container">
      <FlowStepBar phaseId="publish" />
      <PageHeader
        eyebrow="LinkedIn-Studio · Planen & Veröffentlichen"
        title="Freigabe & Versand"
        subtitle="Freigeben und automatisch posten. Slots und Termine werden hier nicht geplant."
        actions={hasActiveLinkedIn ? (
          <span className="badge badge-success" style={{ padding: '6px 12px' }}>LinkedIn verbunden</span>
        ) : undefined}
      />
      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)' }}>
        Freigabe-Warteschlange für Inhalte aus „Text generieren“: genehmigen, terminieren, auf LinkedIn veröffentlichen. Mindestens 7 Tage Vorlauf.
      </p>
      {!darfFreigeben && (
        <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)' }}>
          Genehmigen und Veröffentlichen ist der Rolle Marketing, Vertrieb oder Inhaber:in vorbehalten.
        </p>
      )}

      {linkedInStatus && !hasActiveLinkedIn && (
        <div style={{ marginBottom: '16px' }}>
          <LinkedInConnectionCard variant="compact" status={linkedInStatus} onChanged={() => { loadData(); refresh(); }} />
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          {error}
          <button onClick={() => setError(null)} className="btn btn-sm btn-danger" style={{ marginLeft: '8px' }}>
            &times;
          </button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <label className="form-label" style={{ margin: 0 }}>Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="form-select"
          style={{ width: 'auto' }}
        >
          <option value="">Alle</option>
          <option value="pending">Ausstehend</option>
          <option value="approved">Genehmigt</option>
          <option value="rejected">Abgelehnt</option>
          <option value="posted">Veröffentlicht</option>
        </select>
        <label className="form-label" style={{ margin: 0, marginLeft: '20px' }}>Inhaltstyp:</label>
        <select
          value={contentTypeFilter}
          onChange={(e) => setContentTypeFilter(e.target.value)}
          className="form-select"
          style={{ width: 'auto' }}
        >
          <option value="">Alle</option>
          <option value="linkedin">LinkedIn</option>
          <option value="email">E-Mail</option>
          <option value="blog">Blog</option>
        </select>
        <button onClick={loadData} className="btn btn-info" disabled={loading}>
          {loading ? 'Laden...' : 'Aktualisieren'}
        </button>
      </div>

      {loading && posts.length === 0 ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span className="loading-text">Freigabe-Warteschlange wird geladen…</span>
        </div>
      ) : posts.length === 0 ? (
        <div className="empty-state ui-empty-state">
          <div className="empty-state-title">Keine Beiträge in der Freigabe-Warteschlange.</div>
          <p className="text-muted">Diese Warteschlange füllt sich aus „Text generieren“ (Beitrag planen). LinkedIn-Beiträge aus dem Studio landen im Veröffentlichungskalender.</p>
        </div>
      ) : (
        <div className="grid-cards">
          {posts.map((post) => {
            const daysUntil = getDaysUntilScheduled(post.scheduledDate);
            const isEditing = editingPostId === post.id;
            const isEditingDate = editingDatePostId === post.id;
            const isPublishing = publishingPostId === post.id;

            return (
              <div key={post.id} className="card" data-focus-id={post.id}>
                {/* Header */}
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span className={getStatusBadgeClass(post.status)}>
                      {getStatusLabel(post.status)}
                    </span>
                    <span className="badge badge-secondary" style={{ fontSize: '11px' }}>
                      {post.contentType === 'linkedin' ? 'LinkedIn' : post.contentType === 'email' ? 'E-Mail' : 'Blog'}
                    </span>
                    {post.status !== 'posted' && post.status !== 'rejected' && daysUntil >= 0 && (
                      <span className="text-sm text-secondary">
                        noch {daysUntil} Tage
                      </span>
                    )}
                    {daysUntil < 0 && post.status !== 'posted' && post.status !== 'rejected' && (
                      <span className="badge badge-error" style={{ fontSize: '11px' }}>
                        Überfällig
                      </span>
                    )}
                    {post.postIdeaId && (
                      <RelatedLinks items={ideaBacklinks(post.postIdeaId, { omit: ['text'] })} label="Verknüpft mit dieser Idee" />
                    )}
                    {(post as any).linkedInPostUrl && (
                      <a href={(post as any).linkedInPostUrl} target="_blank" rel="noopener noreferrer"
                         className="badge badge-info" style={{ textDecoration: 'none', fontSize: '11px' }}>
                        Auf LinkedIn
                      </a>
                    )}
                  </div>
                  <div className="text-xs text-muted">
                    {isEditingDate ? (
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <input
                          type="datetime-local"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          className="form-input"
                          style={{ fontSize: '12px', padding: '2px 6px' }}
                        />
                        <button onClick={handleSaveDateEdit} className="btn btn-sm btn-success">OK</button>
                        <button onClick={() => setEditingDatePostId(null)} className="btn btn-sm btn-danger">X</button>
                      </div>
                    ) : (
                      <div>
                        Geplant: {formatDate(post.scheduledDate)}
                        {post.status === 'pending' && (
                          <button
                            onClick={() => handleStartDateEdit(post)}
                            className="btn btn-sm"
                            style={{ marginLeft: '4px', padding: '0 4px', fontSize: '10px' }}
                          >
                            Ändern
                          </button>
                        )}
                      </div>
                    )}
                    <div>Erstellt: {formatDate(post.createdAt)}</div>
                  </div>
                </div>

                {/* Content */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 className="card-title">Inhalt</h3>
                  {isEditing ? (
                    <div>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="form-textarea"
                        style={{ minHeight: '150px', fontFamily: 'monospace', fontSize: '13px' }}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button onClick={handleSaveEdit} className="btn btn-success" disabled={loading}>
                          Speichern
                        </button>
                        <button onClick={() => setEditingPostId(null)} className="btn btn-danger">
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="code-block" style={{ whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto' }}>
                        {post.generatedContent.content}
                      </div>
                      {post.status === 'pending' && (
                        <button
                          onClick={() => handleStartEdit(post)}
                          className="btn btn-sm"
                          style={{ marginTop: '8px', fontSize: '12px' }}
                        >
                          Inhalt bearbeiten
                        </button>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-muted" style={{ marginTop: '8px' }}>
                    <strong>Typ:</strong> {post.generatedContent.type} |{' '}
                    <strong>Provider:</strong> {post.generatedContent.provider} ({post.generatedContent.model})
                  </div>
                </div>

                {/* Approval/Rejection Info */}
                {post.approvedBy && (
                  <div className="alert alert-success text-sm" style={{ marginBottom: '8px' }}>
                    Genehmigt von {post.approvedBy} am {formatDate(post.approvedAt!)}
                  </div>
                )}

                {post.rejectedBy && (
                  <div className="alert alert-error text-sm" style={{ marginBottom: '8px' }}>
                    Abgelehnt von {post.rejectedBy} am {formatDate(post.rejectedAt!)}
                    {post.rejectionReason && (
                      <div style={{ marginTop: '4px' }}>Grund: {post.rejectionReason}</div>
                    )}
                  </div>
                )}

                {post.postedAt && (
                  <div className="alert alert-info text-sm" style={{ marginBottom: '8px' }}>
                    Veröffentlicht am {formatDate(post.postedAt)}
                  </div>
                )}

                {/* Publish Error */}
                {(post as any).publishError && (
                  <div className="alert alert-error text-sm" style={{ marginBottom: '8px' }}>
                    LinkedIn-Fehler: {(post as any).publishError}
                  </div>
                )}

                {/* Actions */}
                <div className="card-footer" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {post.status === 'pending' && darfFreigeben && (
                    <>
                      <button onClick={() => handleApprove(post.id)} className="btn btn-success" disabled={loading}>
                        Genehmigen
                      </button>
                      <button onClick={() => handleReject(post.id)} className="btn btn-danger" disabled={loading}>
                        Ablehnen
                      </button>
                    </>
                  )}
                  {post.status === 'approved' && !post.postedAt && darfFreigeben && (
                    <>
                      {hasActiveLinkedIn && (
                        <button
                          onClick={() => handlePublishToLinkedIn(post.id)}
                          className="btn btn-primary"
                          disabled={isPublishing || loading}
                        >
                          {isPublishing ? 'Wird veröffentlicht...' : 'Auf LinkedIn posten'}
                        </button>
                      )}
                      <button onClick={() => handleMarkAsPosted(post.id)} className="btn btn-info" disabled={loading}>
                        Manuell als gepostet markieren
                      </button>
                    </>
                  )}
                  {post.status !== 'posted' && (
                    <button onClick={() => handleDelete(post.id)} className="btn btn-sm btn-danger" disabled={loading} style={{ marginLeft: 'auto' }}>
                      Löschen
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScheduledPostsPage;
